import landingsEs from './landings_es.json';
import landingsDe from './landings_de.json';
import landingsEn from './landings_en.json';
import levelsDe from './levels_de.json';
import levelsEn from './levels_en.json';

export const SITE_URL = 'https://spanischenlared.com';

/** Páginas de inicio por idioma (el español se queda en la raíz). */
export const HOME_PATHS = { es: '/', de: '/de', en: '/en' };

/** Landings nuevas gestionadas por configuración. */
export const LANDINGS = [...landingsEs, ...landingsDe, ...landingsEn, ...levelsDe, ...levelsEn];

/**
 * Equivalencias entre idiomas por "grupo" de servicio. La DELE española es una
 * página que ya existía (/preparacion-dele) y se reutiliza tal cual.
 */
export const EQUIVALENTS = {
  home: HOME_PATHS,
  dele: {
    es: '/preparacion-dele',
    de: '/de/dele-pruefungsvorbereitung-online',
    en: '/en/dele-exam-preparation-online',
  },
  private: {
    es: '/clases-particulares-espanol-online',
    de: '/de/spanisch-einzelunterricht-online',
    en: '/en/private-spanish-lessons-online',
  },
  conversation: {
    es: '/clases-conversacion-espanol-online',
    de: '/de/spanisch-konversation-online',
    en: '/en/spanish-conversation-online',
  },
  living: {
    es: '/espanol-para-vivir-en-espana',
    de: '/de/spanisch-fuer-auswanderer-spanien',
    en: '/en/spanish-for-living-in-spain',
  },
  'level-a1': { es: '/espanol-a1', de: '/de/spanisch-a1-online', en: '/en/spanish-a1-online' },
  'level-a2': { es: '/espanol-a2', de: '/de/spanisch-a2-online', en: '/en/spanish-a2-online' },
  'level-b1': { es: '/espanol-b1', de: '/de/spanisch-b1-online', en: '/en/spanish-b1-online' },
  'level-b2': { es: '/espanol-b2', de: '/de/spanisch-b2-online', en: '/en/spanish-b2-online' },
  'level-c1c2': { es: '/espanol-c1-c2', de: '/de/spanisch-c1-c2-online', en: '/en/spanish-c1-c2-online' },
};

/** Grupo de equivalencias de cada página de nivel española. */
export const LEVEL_GROUPS = {
  'espanol-a1': 'level-a1',
  'espanol-a2': 'level-a2',
  'espanol-b1': 'level-b1',
  'espanol-b2': 'level-b2',
  'espanol-c1-c2': 'level-c1c2',
};

/** Ruta del nivel en el idioma activo. */
export const levelPathFor = (esSlug, lang) => {
  const group = LEVEL_GROUPS[esSlug];
  return (group && EQUIVALENTS[group][lang]) || `/${esSlug}`;
};

const byPath = Object.fromEntries(LANDINGS.map((l) => [l.path, l]));

export const getLanding = (path) => byPath[path];

export const getLandingBySlug = (lang, slug) =>
  LANDINGS.find((l) => l.lang === lang && l.slug === slug);

/** Idioma a partir de la URL: /de/... -> de, /en/... -> en, resto -> es. */
export const langFromPath = (pathname) => {
  if (pathname === '/de' || pathname.startsWith('/de/')) return 'de';
  if (pathname === '/en' || pathname.startsWith('/en/')) return 'en';
  return 'es';
};

/** Grupo de equivalencias al que pertenece una URL (si lo tiene). */
export const groupFromPath = (pathname) => {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (Object.values(HOME_PATHS).includes(clean)) return 'home';
  const landing = byPath[clean];
  if (landing) return landing.group;
  if (clean === '/preparacion-dele') return 'dele';
  if (LEVEL_GROUPS[clean.slice(1)]) return LEVEL_GROUPS[clean.slice(1)];
  return null;
};

/** URL equivalente en otro idioma; si no existe, la home de ese idioma. */
export const equivalentPath = (pathname, targetLang) => {
  const group = groupFromPath(pathname);
  if (group && EQUIVALENTS[group] && EQUIVALENTS[group][targetLang]) {
    return EQUIVALENTS[group][targetLang];
  }
  return HOME_PATHS[targetLang];
};

/** Alternativas hreflang (absolutas) de una URL, solo entre equivalentes reales. */
export const hreflangFor = (pathname) => {
  const group = groupFromPath(pathname);
  if (!group || !EQUIVALENTS[group]) return [];
  const map = EQUIVALENTS[group];
  const alternates = ['es', 'de', 'en']
    .filter((l) => map[l])
    .map((l) => ({ lang: l, url: `${SITE_URL}${map[l] === '/' ? '/' : map[l]}` }));
  if (map.es) alternates.push({ lang: 'x-default', url: `${SITE_URL}${map.es === '/' ? '/' : map.es}` });
  return alternates;
};

/** Tarjetas "clases según tu objetivo" de cada home. */
export const SERVICE_CARDS = {
  es: {
    kicker: 'Clases según tu objetivo',
    title: '¿Qué quieres conseguir con tu español?',
    lead: 'Cuatro formas de trabajar conmigo, según lo que necesites ahora mismo. Todas son clases individuales y 100 % online.',
    more: 'Más información',
    cards: [
      { title: 'Preparación DELE online', description: 'Trabaja las diferentes partes del examen con una profesora que también es examinadora DELE.', path: '/preparacion-dele' },
      { title: 'Clases particulares de español online', description: 'Clases individuales adaptadas a tu nivel, tus necesidades y tus objetivos.', path: '/clases-particulares-espanol-online' },
      { title: 'Conversación en español online', description: 'Si entiendes español pero te cuesta reaccionar cuando alguien te habla, trabajaremos la conversación, la pronunciación y la confianza.', path: '/clases-conversacion-espanol-online' },
      { title: 'Español para vivir en España', description: 'Médico, banco, vivienda, llamadas, compras, vecinos, trámites… el español que necesitas para desenvolverte con más seguridad.', path: '/espanol-para-vivir-en-espana' },
    ],
  },
  de: {
    kicker: 'Unterricht nach Zielen',
    title: 'Online-Spanischunterricht für deine Ziele',
    lead: 'Vier Wege, mit mir zu arbeiten – je nachdem, was du gerade brauchst. Immer Einzelunterricht und 100 % online.',
    more: 'Mehr erfahren',
    cards: [
      { title: 'DELE-Prüfungsvorbereitung', description: 'Gezielte Vorbereitung A1–C2 mit akkreditierter DELE-Prüferin.', path: '/de/dele-pruefungsvorbereitung-online' },
      { title: 'Spanisch Einzelunterricht', description: 'Privatunterricht 1:1, abgestimmt auf dein Niveau und deine Ziele.', path: '/de/spanisch-einzelunterricht-online' },
      { title: 'Spanisch Konversation', description: 'Sprechtraining, um Hemmungen abzubauen und flüssiger zu sprechen.', path: '/de/spanisch-konversation-online' },
      { title: 'Spanisch für das Leben in Spanien', description: 'Das Spanisch für Wohnung, Arzt, Bank und Behörden in Spanien.', path: '/de/spanisch-fuer-auswanderer-spanien' },
    ],
  },
  en: {
    kicker: 'Lessons by goal',
    title: 'Online Spanish lessons for your goals',
    lead: 'Four ways to work with me, depending on what you need right now. Always one-to-one and 100% online.',
    more: 'Learn more',
    cards: [
      { title: 'DELE Exam Preparation', description: 'Targeted A1–C2 preparation with an accredited DELE examiner.', path: '/en/dele-exam-preparation-online' },
      { title: 'Private Online Spanish Lessons', description: 'One-to-one lessons shaped around your level, goals and pace.', path: '/en/private-spanish-lessons-online' },
      { title: 'Online Spanish Conversation Classes', description: 'Guided speaking practice to build fluency and confidence.', path: '/en/spanish-conversation-online' },
      { title: 'Spanish for Living in Spain', description: 'The everyday Spanish for housing, doctors, banks and paperwork.', path: '/en/spanish-for-living-in-spain' },
    ],
  },
};
